import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { interval, Subscription, BehaviorSubject } from 'rxjs';
import { HttpService } from '../utils/http.service';
import { DeviceService } from '../utils/device.service';
import { StorageService } from './storage.service';
import { AuthEventService } from './auth-event.service';
import { AuthService } from './auth.service';
import { AuthConfigService } from '../utils/auth-config.service';

export interface DeviceSessionStatus {
  isValid: boolean;
  lastActivity?: Date;
  timeoutMinutes?: number;
  error?: string;
}

/**
 * DeviceSessionService
 * 
 * Simplified device-based session management service that:
 * - Checks device session validity every minute
 * - Uses device-session-check endpoint instead of userappnav
 * - Handles automatic logout on session expiry
 * - Manages session state without complex activity tracking
 */
@Injectable({
  providedIn: 'root'
})
export class DeviceSessionService implements OnDestroy, OnInit {
  private readonly CHECK_INTERVAL = 30 * 60 * 1000; // 1 minute
  private checkSubscription: Subscription | null = null;
  private isRunning = false;
  public firstTime = true; // Track if this is the first session check

  // Activity tracking properties
  private lastActivityTime: number = 0;
  private lastSentActivityTime: number = 0;
  private isBackgroundMode: boolean = false;
  private pendingActivityUpdate: boolean = false;

  // Activity listener management
  private activityListenersSetup: boolean = false;
  private recordActivityThrottled: () => void;
  public deviceInfo: any;

  // Subject to emit session status updates
  private sessionStatusSubject = new BehaviorSubject<DeviceSessionStatus>({ isValid: false });
  public sessionStatus$ = this.sessionStatusSubject.asObservable();

  constructor(
    private httpService: HttpService,
    private storageService: StorageService,
    private authEventService: AuthEventService,
    private authConfigService: AuthConfigService,
    private deviceService: DeviceService,
    private authService: AuthService
  ) {
    this.loadLastActivityTime();
  }

  async ngOnInit() {
    // Initialization logic if needed
    this.deviceInfo = await this.deviceService.getDeviceInfo();
  }


  /**
   * Start periodic session checks
   */
  private startChecking(): void {
    if (this.isRunning) {
      // console.log('DeviceSessionService: Already running, skipping start');
      return;
    }

    console.log('DeviceSessionService: Starting periodic session checks every', this.CHECK_INTERVAL / 1000, 'seconds');
    
    // Initialize activity time to current time
    this.lastActivityTime = Date.now();
    
    // Set up global activity listeners when starting session monitoring
    this.setupGlobalActivityListeners();
        
    // Perform immediate check
    this.performSessionCheck();
    
    // Start interval checking
    this.checkSubscription = interval(this.CHECK_INTERVAL).subscribe(() => {
      this.performSessionCheck();
    });
    
    this.isRunning = true;
  }

  public initialiazeFirstTime() {
    // console.log('DeviceSessionService: Initializing first time session check after: ', this.CHECK_INTERVAL / 1000, 'seconds');
    this.firstTime = false; // Reset first time flag
    this.isRunning = false;
    this.startChecking(); // Start checking immediately
  }

  /**
   * Stop periodic session checks
   */
  public stopChecking(): void {
    if (!this.isRunning) {
      // console.log('DeviceSessionService: Not running, nothing to stop');
      return;
    }

    this.firstTime = true; // Reset first time flag
    console.log('DeviceSessionService: Stopping periodic session checks');
    
    // Remove global activity listeners when stopping session monitoring
    this.removeGlobalActivityListeners();
    
    if (this.checkSubscription) {
      this.checkSubscription.unsubscribe();
      this.checkSubscription = null;
    }
    
    this.isRunning = false;
    
    // Reset session status
    this.sessionStatusSubject.next({ isValid: false });
  }

  /**
   * Perform a single session check with optional activity update
   */
  private async performSessionCheck(): Promise<void> {
    // return;
    try {
      const token = this.storageService.getToken();
      if (!token && this.firstTime) {
        return;
      }

      // GT-1412: Skip session/inactivity checks while MFA is pending (prevents logout on MFA page background)
      if (token && !this.authService.isMfaValidated()) {
        console.log('[MFA Guard] Skipping device session check while MFA pending');
        return;
      }

      console.log('TOKEN FROM DEVICE SESSION SERVICE: ', token);

      // Check for local inactivity first (no backend dependency)
      const currentTime = Date.now();
      const inactivityTimeout = this.authConfigService.getInactivityTimeout();
      console.log('DeviceSessionService: Checking inactivity timeout:', {
        lastActivity: this.lastActivityTime,
        currentTime: currentTime,
        inactivityTimeout: inactivityTimeout
      });

      if (this.lastActivityTime > 0 && (currentTime - this.lastActivityTime) > inactivityTimeout) {
        const timeoutSeconds = inactivityTimeout / 1000;
        console.log(`DeviceSessionService: User inactive for ${timeoutSeconds} seconds, logging out...`);
        this.authEventService.emitInactivityLogout(`User inactive for ${timeoutSeconds} seconds`);
        return;
      }

      const deviceInfo = await this.deviceService.getDeviceInfo();
      const deviceFingerprint = this.storageService.get('deviceFingerprint') || deviceInfo?.deviceFingerprint;

      if (!token || !deviceFingerprint) {
        // console.log('DeviceSessionService: No token or device fingerprint, marking session as invalid');
        this.handleSessionInvalid('No authentication data');
        return;
      }

      console.log('DeviceSessionService: Checking device session with activity update...');

      const payload = {
        token: token,
        device_fingerprint: deviceFingerprint || '',
        user_device_id: this.storageService.get('user_device_id') || null,
        user_session_id: this.storageService.get('user_session_id') || null
      };

      // Add activity timestamp if we have pending activity update
      if (this.lastActivityTime > 0) {
        const activityValidation = this.validateActivityUpdate();
        if (activityValidation.isValid) {
          payload['last_activity'] = Math.floor(this.lastActivityTime / 1000); // Convert to seconds
          // console.log('DeviceSessionService: Including activity update in session check');
        } else {
          console.log('DeviceSessionService: Activity update validation failed:', activityValidation.reason);
        }
      }

      const response = await this.httpService.post('device-session-check', payload).toPromise();
      
      if (response && response.status === 'valid') {
        console.log('DeviceSessionService: Session is valid');
        
        // Mark activity as sent if we included it
        if (payload['last_activity']) {
          this.lastSentActivityTime = this.lastActivityTime;
          this.pendingActivityUpdate = false;
          this.storageService.set('last_sent_activity_time', this.lastActivityTime);
        }
        
        this.sessionStatusSubject.next({
          isValid: true,
          lastActivity: response.last_activity ? new Date(response.last_activity) : undefined,
          timeoutMinutes: response.timeout_minutes
        });
      } else {
        console.warn('DeviceSessionService: Session check returned invalid status');
        this.handleSessionInvalid('Session validation failed');
      }

    } catch (error: any) {
      console.error('DeviceSessionService: Session check failed:', error);
      
      // Check if it's a 401 error from userDeviceController
      if (error.status !== 'valid' || error.code === 401) {
        // Emit 'token_expired' to match the auth event switch statement
        this.sessionStatusSubject.next({
          isValid: false,
          error: 'token_expired'
        });
      }
    }
  }

  /**
   * Handle session invalidation
   */
  private handleSessionInvalid(reason: string): void {
    console.log('DeviceSessionService: Session invalid -', reason);
    
    // Update session status
    this.sessionStatusSubject.next({
      isValid: false,
      error: reason
    });
    
    // Stop checking
    this.stopChecking();
  }

  /**
   * Manually trigger a session check
   */
  public async checkSessionNow(): Promise<DeviceSessionStatus> {
    await this.performSessionCheck();
    return this.sessionStatusSubject.value;
  }

  /**
   * Get current session status
   */
  public getCurrentStatus(): DeviceSessionStatus {
    return this.sessionStatusSubject.value;
  }

  /**
   * Check if session checking is currently running
   */
  public isCheckingRunning(): boolean {
    return this.isRunning;
  }

  /**
   * Record user activity and mark for next session check
   */
  public async recordActivity(type: 'user_interaction' | 'app_foreground' | 'manual' = 'user_interaction'): Promise<void> {
    const currentTime = Date.now();
    // console.log('DeviceSessionService: Recording activity type:', type);
    
    // Update local activity time
    this.lastActivityTime = currentTime;
    this.storageService.set('last_activity_time', currentTime);
    
    // IMPORTANT: Also update session state for IdleService compatibility
    try {
      await this.storageService.recordActivity();
      // console.log('DeviceSessionService: Activity recorded to session state successfully');
    } catch (error) {
      console.error('DeviceSessionService: Error recording activity to session state:', error);
    }
    
    // Mark that we have pending activity to send with next session check
    this.pendingActivityUpdate = true;
  }

  /**
   * Validate if an activity update should be sent based on inactivity rules
   */
  private validateActivityUpdate(): { isValid: boolean; reason?: string } {
    const currentTime = Date.now();
    const authMode = this.authConfigService.getCurrentAuthMode();
    
    // Get inactivity timeout based on current mode and background state
    let inactivityTimeout: number;
    if (this.isBackgroundMode && authMode === 'local') {
      inactivityTimeout = this.authConfigService.getBackgroundInactivityTimeout();
    } else {
      inactivityTimeout = this.authConfigService.getInactivityTimeout();
      console.log('inactivity timeout after:', inactivityTimeout);
    }

    // Convert timeout from milliseconds to seconds for comparison
    const inactivityTimeoutSeconds = Math.floor(inactivityTimeout / 1000);

    // Check if enough time has passed since last activity
    if (this.lastActivityTime > 0) {
      const timeSinceLastActivity = Math.floor((currentTime - this.lastActivityTime) / 1000);
      
      // If the user has been inactive longer than the timeout, they should be logged out
      if (timeSinceLastActivity > inactivityTimeoutSeconds) {
        return {
          isValid: false,
          reason: `Inactivity timeout exceeded (${timeSinceLastActivity}s > ${inactivityTimeoutSeconds}s)`
        };
      } else {
        console.log(`DeviceSessionService: Activity update valid (${timeSinceLastActivity}s < ${inactivityTimeoutSeconds}s)`);
        return { isValid: true };
      }
    }

    // Check if enough time has passed since last sent update to avoid spam
    // const minUpdateInterval = 30;
    // // Minimum 30 seconds between updates
    // if (this.lastSentActivityTime > 0) {
    //   const timeSinceLastSent = Math.floor((currentTime - this.lastSentActivityTime) / 1000);
      
    //   if (timeSinceLastSent < minUpdateInterval) {
    //     return {
    //       isValid: false,
    //       reason: `Update too frequent (${timeSinceLastSent}s < ${minUpdateInterval}s)`
    //     };
    //   }
    // }

    // Validate timestamp is not in the future
    const currentTimestamp = Math.floor(currentTime / 1000);
    const activityTimestamp = Math.floor(this.lastActivityTime / 1000);
    if (activityTimestamp > currentTimestamp + 5) {
      return {
        isValid: false,
        reason: 'Activity timestamp is in the future'
      };
    }

    // Validate timestamp is not too old
    const maxAgeSeconds = 3600; // 1 hour
    if (currentTimestamp - activityTimestamp > maxAgeSeconds) {
      return {
        isValid: false,
        reason: 'Activity timestamp is too old'
      };
    }

    return { isValid: true };
  }

  /**
   * Set background mode
   */
  public setBackgroundMode(isBackground: boolean): void {
    this.isBackgroundMode = isBackground;
    console.log('DeviceSessionService: Background mode set to:', isBackground);
  }

  /**
   * Get current activity status
   */
  public getActivityStatus(): {
    lastActivityTime: number;
    lastSentActivityTime: number;
    timeSinceLastActivity: number;
    inactivityTimeout: number;
    isBackgroundMode: boolean;
    authMode: string;
    pendingActivityUpdate: boolean;
  } {
    const currentTime = Date.now();
    const authMode = this.authConfigService.getCurrentAuthMode();
    
    let inactivityTimeout: number;
    if (this.isBackgroundMode && authMode === 'local') {
      inactivityTimeout = this.authConfigService.getBackgroundInactivityTimeout();
    } else {
      inactivityTimeout = this.authConfigService.getInactivityTimeout();
    }

    return {
      lastActivityTime: this.lastActivityTime,
      lastSentActivityTime: this.lastSentActivityTime,
      timeSinceLastActivity: this.lastActivityTime > 0 ? Math.floor((currentTime - this.lastActivityTime) / 1000) : 0,
      inactivityTimeout: Math.floor(inactivityTimeout / 1000),
      isBackgroundMode: this.isBackgroundMode,
      authMode,
      pendingActivityUpdate: this.pendingActivityUpdate
    };
  }

  /**
   * Load last activity time from storage
   */
  private loadLastActivityTime(): void {
    const storedTime = this.storageService.get('last_activity_time');
    const storedSentTime = this.storageService.get('last_sent_activity_time');
    
    if (storedTime) {
      this.lastActivityTime = storedTime;
    }
    
    if (storedSentTime) {
      this.lastSentActivityTime = storedSentTime;
    }
  }

  /**
   * Clear activity tracking data
   */
  public clearActivityData(): void {
    this.lastActivityTime = 0;
    this.lastSentActivityTime = 0;
    this.pendingActivityUpdate = false;
    // Clear device credentials from storage
    this.storageService.remove('user_device_id');
    this.storageService.remove('deviceFingerprint');
    this.storageService.remove('last_activity_time');
    this.storageService.remove('last_sent_activity_time');
    console.log('DeviceSessionService: Activity data cleared');
  }

  /**
   * Setup global activity listeners for user interactions
   */
  private setupGlobalActivityListeners(): void {
    if (this.activityListenersSetup) {
      // console.log('DeviceSessionService: Activity listeners already setup');
      return;
    }

    let activityEvents = this.getActivities();

    // Throttle activity recording to avoid excessive calls
    let lastRecorded = 0;
    const throttleMs = 2000; // 2 seconds throttle

    this.recordActivityThrottled = () => {
      const now = Date.now();
      if (now - lastRecorded > throttleMs) {
        this.recordActivity('user_interaction');
        lastRecorded = now;
      }
    };

    // Add event listeners
    activityEvents.forEach(eventType => {
      document.addEventListener(eventType, this.recordActivityThrottled, { passive: true });
    });

    this.activityListenersSetup = true;
    // console.log('DeviceSessionService: Global activity listeners setup complete');
  }

  private getActivities(): string[] {
    let activityEvents = [
      'click', 'touchstart',
      'keydown', 'scroll', 'mousedown', 'mousemove',
      'focus', 'input'
    ];

    let deviceIsMobile = this.deviceService.isMobile();
    if (deviceIsMobile) {
      // Common user interaction events
      activityEvents = [
        'click', 'touchstart', 'touchend', 'touchmove',
        'keydown', 'scroll',
        'focus', 'input'
      ];
    }
    // this.storageService.setDexie('data', 'activityEvents', activityEvents);

    return activityEvents;
  }

  /**
   * Remove global activity listeners
   */
  private removeGlobalActivityListeners(): void {
    if (!this.activityListenersSetup || !this.recordActivityThrottled) {
      // console.log('DeviceSessionService: No activity listeners to remove');
      return;
    }

    // Common user interaction events (same as setup)
    const activityEvents = this.getActivities();

    // Remove event listeners
    activityEvents.forEach(eventType => {
      document.removeEventListener(eventType, this.recordActivityThrottled, { passive: true } as any);
    });

    this.activityListenersSetup = false;
    this.recordActivityThrottled = null;
    // console.log('DeviceSessionService: Global activity listeners removed');
  }

  ngOnDestroy(): void {
    // console.log('DeviceSessionService: Destroying service');
    this.stopChecking();
    this.removeGlobalActivityListeners();
  }
}
